import type { IngestJobUi } from "../services/ragDocumentApi";
import { isMetadataStatusPending } from "../services/ragDocumentApi";
import type { PreviewItemUi } from "../types/documentManagementUiTypes";

const STORAGE_KEY = "opva.dm.ingestSession";

export type PersistedIngestSession = {
  v: 1;
  excludedDocumentIds: number[];
  ingestJobs: Record<number, IngestJobUi>;
  metadataJobs?: Record<number, IngestJobUi>;
  /** document_id → metadata_job_id for rehydrate after refresh */
  metadataJobByDocumentId?: Record<number, number>;
};

function normalizeJobMap(
  raw: Record<string, IngestJobUi> | Record<number, IngestJobUi> | undefined
): Record<number, IngestJobUi> {
  if (!raw) {
    return {};
  }
  const out: Record<number, IngestJobUi> = {};
  for (const [key, job] of Object.entries(raw)) {
    const jobId = Number(key);
    if (!Number.isFinite(jobId) || !job) {
      continue;
    }
    out[jobId] = {
      ...job,
      jobId: job.jobId ?? jobId,
      documentId: job.documentId,
    };
  }
  return out;
}

function normalizeJobByDocumentId(
  raw: Record<string, number> | Record<number, number> | undefined
): Record<number, number> {
  if (!raw) {
    return {};
  }
  const out: Record<number, number> = {};
  for (const [key, jobId] of Object.entries(raw)) {
    const documentId = Number(key);
    if (!Number.isFinite(documentId) || !Number.isFinite(jobId) || jobId <= 0) {
      continue;
    }
    out[documentId] = jobId;
  }
  return out;
}

export function metadataJobByDocumentIdFromPreview(
  items: PreviewItemUi[] | null | undefined
): Record<number, number> {
  const out: Record<number, number> = {};
  for (const item of items ?? []) {
    const jobId = item.metadata_job_id;
    if (jobId != null && jobId > 0) {
      out[item.document_id] = jobId;
    }
  }
  return out;
}

export function metadataJobByDocumentIdFromMetadataJobs(
  jobs: Record<number, IngestJobUi> | undefined
): Record<number, number> {
  const out: Record<number, number> = {};
  for (const job of Object.values(jobs ?? {})) {
    const documentId = job.documentId;
    if (documentId != null && documentId > 0 && job.jobId > 0) {
      out[documentId] = job.jobId;
    }
  }
  return out;
}

/** Match pending preview rows to persisted jobs by file name when document map was lost. */
export function metadataJobByDocumentIdFromFileNames(
  items: PreviewItemUi[],
  jobs: Record<number, IngestJobUi>
): Record<number, number> {
  const byFileName = new Map<string, number>();
  for (const job of Object.values(jobs)) {
    const key = job.fileName.trim().toLowerCase();
    if (key && job.jobId > 0) {
      byFileName.set(key, job.jobId);
    }
  }
  const out: Record<number, number> = {};
  for (const item of items) {
    const jobId = byFileName.get(item.file_name.trim().toLowerCase());
    if (jobId) {
      out[item.document_id] = jobId;
    }
  }
  return out;
}

function visiblePreviewItems(
  previewItems: PreviewItemUi[] | null | undefined,
  excludedDocumentIds: number[]
): PreviewItemUi[] {
  const excluded = new Set(excludedDocumentIds);
  return (previewItems ?? []).filter((item) => !excluded.has(item.document_id));
}

/** Keep only metadata jobs tied to the current review batch (visible preview rows). */
export function pruneMetadataJobsForPreview(
  previewItems: PreviewItemUi[] | null | undefined,
  metadataJobs: Record<number, IngestJobUi>,
  excludedDocumentIds: number[] = []
): Record<number, IngestJobUi> {
  const visible = visiblePreviewItems(previewItems, excludedDocumentIds);
  if (!visible.length) {
    return {};
  }
  const docIds = new Set(visible.map((item) => item.document_id));
  const jobIdsFromPreview = new Set(
    visible
      .map((item) => item.metadata_job_id)
      .filter((id): id is number => id != null && id > 0)
  );
  const out: Record<number, IngestJobUi> = {};
  for (const job of Object.values(metadataJobs)) {
    const matchesDoc =
      job.documentId != null && docIds.has(job.documentId);
    const matchesJobId = jobIdsFromPreview.has(job.jobId);
    if (matchesDoc || matchesJobId) {
      out[job.jobId] = job;
    }
  }
  return out;
}

/** Prune stale jobs and seed entries from preview `metadata_job_id` (upload / rehydrate). */
export function mergeMetadataJobsForPreview(
  previewItems: PreviewItemUi[] | null | undefined,
  metadataJobs: Record<number, IngestJobUi>,
  excludedDocumentIds: number[] = []
): Record<number, IngestJobUi> {
  const visible = visiblePreviewItems(previewItems, excludedDocumentIds);
  const out = pruneMetadataJobsForPreview(
    previewItems,
    metadataJobs,
    excludedDocumentIds
  );
  for (const item of visible) {
    const jobId = item.metadata_job_id;
    if (jobId == null || jobId <= 0) {
      continue;
    }
    const existing = out[jobId];
    out[jobId] = {
      jobId,
      fileName: item.file_name || existing?.fileName || "Document",
      documentId: item.document_id ?? existing?.documentId,
      last: existing?.last ?? null,
    };
  }
  return out;
}

export function previewHasActiveMetadataBatch(
  previewItems: PreviewItemUi[] | null | undefined,
  excludedDocumentIds: number[] = []
): boolean {
  const visible = visiblePreviewItems(previewItems, excludedDocumentIds);
  return visible.some(
    (item) =>
      (item.metadata_job_id != null && item.metadata_job_id > 0) ||
      !!item.metadata_pending ||
      isMetadataStatusPending(item.metadata_status)
  );
}

/** document_id → job_id for the active review batch only (no historical merge). */
export function buildMetadataJobByDocumentIdForSession(
  previewItems: PreviewItemUi[] | null | undefined,
  metadataJobs: Record<number, IngestJobUi>,
  excludedDocumentIds: number[] = []
): Record<number, number> {
  const visible = visiblePreviewItems(previewItems, excludedDocumentIds);
  if (!visible.length) {
    return {};
  }
  const prunedJobs = mergeMetadataJobsForPreview(
    previewItems,
    metadataJobs,
    excludedDocumentIds
  );
  const fromPreview = metadataJobByDocumentIdFromPreview(visible);
  const fromJobs = metadataJobByDocumentIdFromMetadataJobs(prunedJobs);
  const out: Record<number, number> = {};
  for (const item of visible) {
    const jobId =
      fromPreview[item.document_id] ??
      item.metadata_job_id ??
      fromJobs[item.document_id];
    if (jobId != null && jobId > 0) {
      out[item.document_id] = jobId;
    }
  }
  if (!Object.keys(out).length && Object.keys(prunedJobs).length) {
    return metadataJobByDocumentIdFromFileNames(visible, prunedJobs);
  }
  return out;
}

/** @deprecated Use buildMetadataJobByDocumentIdForSession (no historical merge). */
export function mergePersistedMetadataJobByDocumentId(
  previewItems: PreviewItemUi[] | null | undefined,
  _existing: Record<number, number> | undefined,
  metadataJobs: Record<number, IngestJobUi> | undefined
): Record<number, number> {
  return buildMetadataJobByDocumentIdForSession(
    previewItems,
    metadataJobs ?? {},
    []
  );
}

export function metadataJobsKeySignature(
  jobs: Record<number, IngestJobUi>
): string {
  return Object.keys(jobs)
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b)
    .join(",");
}

export function resolveMetadataJobByDocumentId(
  pendingItems: PreviewItemUi[],
  metadataJobs: Record<number, IngestJobUi>,
  excludedDocumentIds: number[] = [],
  persistedJobByDocumentId?: Record<number, number>
): Record<number, number> {
  const prunedJobs = mergeMetadataJobsForPreview(
    pendingItems,
    metadataJobs,
    excludedDocumentIds
  );
  const built = buildMetadataJobByDocumentIdForSession(
    pendingItems,
    prunedJobs,
    excludedDocumentIds
  );
  const pendingDocIds = new Set(pendingItems.map((item) => item.document_id));
  const out: Record<number, number> = { ...built };
  for (const [docKey, jobId] of Object.entries(persistedJobByDocumentId ?? {})) {
    const documentId = Number(docKey);
    if (
      Number.isFinite(documentId) &&
      pendingDocIds.has(documentId) &&
      jobId > 0
    ) {
      out[documentId] = out[documentId] ?? jobId;
    }
  }
  return out;
}

/** Rebuild job state from document→job map when preview rows lack `metadata_job_id` after refresh. */
export function mergeMetadataJobsFromDocumentMap(
  previewItems: PreviewItemUi[],
  metadataJobs: Record<number, IngestJobUi>,
  jobByDocumentId: Record<number, number>,
  excludedDocumentIds: number[] = []
): Record<number, IngestJobUi> {
  const visible = visiblePreviewItems(previewItems, excludedDocumentIds);
  let out = mergeMetadataJobsForPreview(
    previewItems,
    metadataJobs,
    excludedDocumentIds
  );
  for (const item of visible) {
    const jobId =
      jobByDocumentId[item.document_id] ?? item.metadata_job_id ?? null;
    if (jobId == null || jobId <= 0) {
      continue;
    }
    const existing = out[jobId];
    out[jobId] = {
      jobId,
      fileName: item.file_name || existing?.fileName || "Document",
      documentId: item.document_id ?? existing?.documentId,
      last: existing?.last ?? null,
    };
  }
  return out;
}

export type IngestSessionPersistInput = {
  excludedDocumentIds: number[];
  ingestJobs: Record<number, IngestJobUi>;
  metadataJobs: Record<number, IngestJobUi>;
  previewItems: PreviewItemUi[] | null | undefined;
};

/** Never persist empty metadata maps over a non-empty session during an active detect batch. */
export function buildIngestSessionPayloadForPersist(
  input: IngestSessionPersistInput
): {
  excludedDocumentIds: number[];
  ingestJobs: Record<number, IngestJobUi>;
  metadataJobs: Record<number, IngestJobUi>;
  metadataJobByDocumentId: Record<number, number>;
} {
  const existing = readPersistedIngestSession();
  const { excludedDocumentIds, ingestJobs, metadataJobs, previewItems } = input;
  const visible = visiblePreviewItems(previewItems, excludedDocumentIds);

  let jobsToWrite = metadataJobsForSessionBootstrap(
    previewItems,
    metadataJobs,
    excludedDocumentIds
  );

  if (
    visible.length > 0 &&
    Object.keys(jobsToWrite).length === 0 &&
    existing?.metadataJobs &&
    Object.keys(existing.metadataJobs).length > 0
  ) {
    jobsToWrite = mergeMetadataJobsForPreview(
      previewItems,
      existing.metadataJobs,
      excludedDocumentIds
    );
  }

  let jobByDocumentId =
    visible.length > 0
      ? buildMetadataJobByDocumentIdForSession(
          previewItems,
          jobsToWrite,
          excludedDocumentIds
        )
      : { ...(existing?.metadataJobByDocumentId ?? {}) };

  if (
    visible.length > 0 &&
    Object.keys(jobByDocumentId).length === 0 &&
    existing?.metadataJobByDocumentId &&
    Object.keys(existing.metadataJobByDocumentId).length > 0
  ) {
    jobByDocumentId = { ...existing.metadataJobByDocumentId };
    for (const item of visible) {
      const fromPreview = item.metadata_job_id;
      if (fromPreview != null && fromPreview > 0) {
        jobByDocumentId[item.document_id] = fromPreview;
      }
    }
  }

  if (Object.keys(jobsToWrite).length === 0 && Object.keys(jobByDocumentId).length > 0) {
    jobsToWrite = mergeMetadataJobsFromDocumentMap(
      previewItems ?? [],
      jobsToWrite,
      jobByDocumentId,
      excludedDocumentIds
    );
  }

  return {
    excludedDocumentIds,
    ingestJobs,
    metadataJobs: jobsToWrite,
    metadataJobByDocumentId: jobByDocumentId,
  };
}

/** Jobs to keep in session/memory while preview is not loaded yet (refresh bootstrap). */
export function metadataJobsForSessionBootstrap(
  previewItems: PreviewItemUi[] | null | undefined,
  metadataJobs: Record<number, IngestJobUi>,
  excludedDocumentIds: number[] = []
): Record<number, IngestJobUi> {
  const visible = visiblePreviewItems(previewItems, excludedDocumentIds);
  if (visible.length > 0) {
    return mergeMetadataJobsForPreview(
      previewItems,
      metadataJobs,
      excludedDocumentIds
    );
  }
  return metadataJobs;
}

export function applyPersistedMetadataJobIds(
  items: PreviewItemUi[],
  jobByDocumentId: Record<number, number>
): PreviewItemUi[] {
  if (!Object.keys(jobByDocumentId).length) {
    return items;
  }
  return items.map((item) => {
    const jobId = jobByDocumentId[item.document_id];
    if (!jobId) {
      return item;
    }
    return {
      ...item,
      metadata_job_id: jobId,
      metadata_pending:
        item.metadata_pending ?? isMetadataStatusPending(item.metadata_status),
    };
  });
}

export function readPersistedIngestSession(): PersistedIngestSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PersistedIngestSession;
    if (parsed?.v !== 1 || !Array.isArray(parsed.excludedDocumentIds)) {
      return null;
    }
    return {
      v: 1,
      excludedDocumentIds: parsed.excludedDocumentIds.filter((id) =>
        Number.isFinite(id)
      ),
      ingestJobs: normalizeJobMap(parsed.ingestJobs),
      metadataJobs: normalizeJobMap(parsed.metadataJobs),
      metadataJobByDocumentId: normalizeJobByDocumentId(
        parsed.metadataJobByDocumentId
      ),
    };
  } catch {
    return null;
  }
}

export function writePersistedIngestSession(session: {
  excludedDocumentIds: number[];
  ingestJobs: Record<number, IngestJobUi>;
  metadataJobs?: Record<number, IngestJobUi>;
  metadataJobByDocumentId?: Record<number, number>;
}): void {
  try {
    const payload: PersistedIngestSession = {
      v: 1,
      excludedDocumentIds: session.excludedDocumentIds,
      ingestJobs: session.ingestJobs,
      metadataJobs: session.metadataJobs,
      metadataJobByDocumentId: session.metadataJobByDocumentId,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

export function clearPersistedIngestSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function hasPersistedMetadataSession(
  metadataJobs: Record<number, IngestJobUi>
): boolean {
  return Object.keys(metadataJobs).length > 0;
}

export type MetadataJobTargetUi = {
  jobId: number;
  fileName: string;
  documentId?: number;
};

/** Jobs to track for metadata SSE — from session state and/or preview rows. */
export function collectMetadataJobTargets(
  previewItems: PreviewItemUi[],
  metadataJobs: Record<number, IngestJobUi>
): MetadataJobTargetUi[] {
  const previewDocIds = new Set(previewItems.map((item) => item.document_id));
  const byJobId = new Map<number, MetadataJobTargetUi>();

  for (const job of Object.values(metadataJobs)) {
    if (job.jobId <= 0) {
      continue;
    }
    const docId = job.documentId;
    if (docId != null && docId > 0 && !previewDocIds.has(docId)) {
      continue;
    }
    byJobId.set(job.jobId, {
      jobId: job.jobId,
      fileName: job.fileName || "Document",
      documentId: docId,
    });
  }

  for (const item of previewItems) {
    const jobId = item.metadata_job_id;
    if (jobId == null || jobId <= 0) {
      continue;
    }
    const existing = byJobId.get(jobId);
    byJobId.set(jobId, {
      jobId,
      fileName: item.file_name || existing?.fileName || "Document",
      documentId: item.document_id ?? existing?.documentId,
    });
  }

  return Array.from(byJobId.values());
}
